import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  Crown, Settings, Users, CheckCircle, XCircle, 
  Clock, Edit, Save, RefreshCw, Award 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFoundersPanel = () => {
  const { token: authToken } = useAuth();
  
  // State
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [requests, setRequests] = useState([]);
  const [verifiedFounders, setVerifiedFounders] = useState([]);
  const [editingConfig, setEditingConfig] = useState(false);
  const [configUpdates, setConfigUpdates] = useState({});

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = () => {
    fetchConfig();
    fetchRequests();
    fetchVerifiedFounders();
  };

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/founders/config`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      setConfig(data);
      setConfigUpdates(data);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/founders/requests`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      if (data.ok) {
        setRequests(data.requests);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const fetchVerifiedFounders = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/founders/verified`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      if (data.ok) {
        setVerifiedFounders(data.founders);
      }
    } catch (error) {
      console.error('Error fetching verified founders:', error);
    }
  };

  const updateConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/founders/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configUpdates)
      });
      
      const data = await response.json();
      if (data.ok) {
        toast.success('Configuration updated successfully');
        setConfig(data.config);
        setEditingConfig(false);
        fetchAll();
      } else {
        toast.error('Failed to update configuration');
      }
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error('Failed to update configuration');
    } finally {
      setLoading(false);
    }
  };

  const verifyFounder = async (requestId, founderNumber) => {
    if (!founderNumber || founderNumber < 1 || founderNumber > 1728) {
      toast.error('Invalid founder number (must be 1-1728)');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/founders/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          request_id: requestId,
          founder_number: parseInt(founderNumber)
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Founder #${founderNumber} verified successfully`);
        fetchAll();
      } else {
        toast.error(data.error || 'Failed to verify founder');
      }
    } catch (error) {
      console.error('Error verifying founder:', error);
      toast.error('Failed to verify founder');
    } finally {
      setLoading(false);
    }
  };

  const rejectFounder = async (requestId) => {
    if (!confirm('Are you sure you want to reject this request?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/founders/reject/${requestId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Request rejected');
        fetchAll();
      } else {
        toast.error(data.error || 'Failed to reject request');
      }
    } catch (error) {
      console.error('Error rejecting founder:', error);
      toast.error('Failed to reject request');
    } finally {
      setLoading(false);
    }
  };

  const getBadgeColor = (tier) => {
    const colors = {
      'platinum': 'bg-purple-100 text-purple-800',
      'gold': 'bg-yellow-100 text-yellow-800',
      'silver': 'bg-gray-100 text-gray-800',
      'bronze': 'bg-orange-100 text-orange-800',
      'none': 'bg-gray-100 text-gray-600'
    };
    return colors[tier] || colors.none;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'verified': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'expired': 'bg-gray-100 text-gray-600'
    };
    return colors[status] || colors.pending;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Founders Launch Management</h1>
        <p className="text-gray-600">Manage founder requests, verification, and configuration</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="requests">Requests ({requests.filter(r => r.status === 'pending').length})</TabsTrigger>
          <TabsTrigger value="verified">Verified ({verifiedFounders.length})</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <Card>
              <CardContent className="p-6 text-center">
                <Crown className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                <div className="text-3xl font-bold text-gray-900">
                  {verifiedFounders.length}
                </div>
                <div className="text-sm text-gray-600">Verified Founders</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <Clock className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                <div className="text-3xl font-bold text-gray-900">
                  {requests.filter(r => r.status === 'pending').length}
                </div>
                <div className="text-sm text-gray-600">Pending Requests</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <Users className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <div className="text-3xl font-bold text-gray-900">
                  {config?.founder_cap || 1728}
                </div>
                <div className="text-sm text-gray-600">Founder Cap</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <CheckCircle className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                <div className="text-3xl font-bold text-gray-900">
                  {config ? ((verifiedFounders.length / config.founder_cap) * 100).toFixed(1) : 0}%
                </div>
                <div className="text-sm text-gray-600">Progress</div>
              </CardContent>
            </Card>
          </div>

          {/* Progress Bar */}
          <Card>
            <CardHeader>
              <CardTitle>Founder Launch Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full bg-gray-200 rounded-full h-6">
                <div
                  className="bg-blue-600 h-6 rounded-full transition-all duration-500 flex items-center justify-center text-white text-sm font-semibold"
                  style={{ 
                    width: config ? `${(verifiedFounders.length / config.founder_cap) * 100}%` : '0%',
                    minWidth: verifiedFounders.length > 0 ? '50px' : '0px'
                  }}
                >
                  {verifiedFounders.length} / {config?.founder_cap || 1728}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Requests Tab */}
        <TabsContent value="requests">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Pending Founder Requests</CardTitle>
              <Button onClick={fetchRequests} size="sm" variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {requests.filter(r => r.status === 'pending').length === 0 ? (
                <p className="text-center text-gray-500 py-8">No pending requests</p>
              ) : (
                <div className="space-y-4">
                  {requests
                    .filter(r => r.status === 'pending')
                    .map((request) => (
                      <Card key={request.id} className="border-2">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="font-bold text-lg text-gray-900">
                                {request.full_name}
                              </h3>
                              <p className="text-sm text-gray-600">{request.email}</p>
                            </div>
                            <Badge className={getStatusColor(request.status)}>
                              {request.status}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                            <div>
                              <span className="text-gray-600">Wallet:</span>
                              <p className="font-mono text-xs break-all">{request.trc20_wallet}</p>
                            </div>
                            {request.phone && (
                              <div>
                                <span className="text-gray-600">Phone:</span>
                                <p>{request.phone}</p>
                              </div>
                            )}
                            {request.voucher_code && (
                              <div>
                                <span className="text-gray-600">Voucher Code:</span>
                                <p>{request.voucher_code}</p>
                              </div>
                            )}
                            <div>
                              <span className="text-gray-600">Submitted:</span>
                              <p>{formatDate(request.submitted_at)}</p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="1728"
                              placeholder="Founder #"
                              id={`founder-number-${request.id}`}
                              className="w-32"
                            />
                            <Button
                              onClick={() => {
                                const input = document.getElementById(`founder-number-${request.id}`);
                                verifyFounder(request.id, input.value);
                              }}
                              disabled={loading}
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Verify
                            </Button>
                            <Button
                              onClick={() => rejectFounder(request.id)}
                              disabled={loading}
                              variant="destructive"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Verified Tab */}
        <TabsContent value="verified">
          <Card>
            <CardHeader>
              <CardTitle>Verified Founders</CardTitle>
            </CardHeader>
            <CardContent>
              {verifiedFounders.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No verified founders yet</p>
              ) : (
                <div className="space-y-4">
                  {verifiedFounders.map((founder) => (
                    <Card key={founder.id} className="border-2">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <Crown className="h-5 w-5 text-blue-600" />
                              <span className="text-2xl font-bold text-gray-900">
                                #{founder.founder_number}
                              </span>
                              <Badge className={getBadgeColor(founder.badge_tier)}>
                                {founder.badge_tier.toUpperCase()}
                              </Badge>
                            </div>
                            <h3 className="font-bold text-lg text-gray-900">
                              {founder.full_name}
                            </h3>
                            <p className="text-sm text-gray-600">{founder.email}</p>
                          </div>
                          <div className="text-right text-sm text-gray-600">
                            <p>Verified: {formatDate(founder.verified_at)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Founders Launch Configuration</CardTitle>
              {!editingConfig ? (
                <Button onClick={() => setEditingConfig(true)} size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={updateConfig} disabled={loading} size="sm">
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button 
                    onClick={() => {
                      setEditingConfig(false);
                      setConfigUpdates(config);
                    }} 
                    variant="outline" 
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {config && (
                <>
                  {/* Page Status */}
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingConfig ? configUpdates.page_enabled : config.page_enabled}
                        onChange={(e) => setConfigUpdates({...configUpdates, page_enabled: e.target.checked})}
                        disabled={!editingConfig}
                        className="w-4 h-4"
                      />
                      <span className="font-medium">Page Enabled</span>
                    </label>
                  </div>

                  {/* Founder Cap */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Founder Cap
                    </label>
                    <Input
                      type="number"
                      value={editingConfig ? configUpdates.founder_cap : config.founder_cap}
                      onChange={(e) => setConfigUpdates({...configUpdates, founder_cap: parseInt(e.target.value)})}
                      disabled={!editingConfig}
                    />
                  </div>

                  {/* Countdown */}
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer mb-2">
                      <input
                        type="checkbox"
                        checked={editingConfig ? configUpdates.countdown_enabled : config.countdown_enabled}
                        onChange={(e) => setConfigUpdates({...configUpdates, countdown_enabled: e.target.checked})}
                        disabled={!editingConfig}
                        className="w-4 h-4"
                      />
                      <span className="font-medium">Countdown Enabled</span>
                    </label>
                    {(editingConfig ? configUpdates.countdown_enabled : config.countdown_enabled) && (
                      <Input
                        type="datetime-local"
                        value={editingConfig && configUpdates.countdown_end ? new Date(configUpdates.countdown_end).toISOString().slice(0, 16) : ''}
                        onChange={(e) => setConfigUpdates({...configUpdates, countdown_end: new Date(e.target.value).toISOString()})}
                        disabled={!editingConfig}
                      />
                    )}
                  </div>

                  {/* Feature Toggles */}
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingConfig ? configUpdates.phone_field_enabled : config.phone_field_enabled}
                        onChange={(e) => setConfigUpdates({...configUpdates, phone_field_enabled: e.target.checked})}
                        disabled={!editingConfig}
                        className="w-4 h-4"
                      />
                      <span className="font-medium">Phone Field Enabled</span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingConfig ? configUpdates.progress_bar_enabled : config.progress_bar_enabled}
                        onChange={(e) => setConfigUpdates({...configUpdates, progress_bar_enabled: e.target.checked})}
                        disabled={!editingConfig}
                        className="w-4 h-4"
                      />
                      <span className="font-medium">Progress Bar Enabled</span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingConfig ? configUpdates.ticker_enabled : config.ticker_enabled}
                        onChange={(e) => setConfigUpdates({...configUpdates, ticker_enabled: e.target.checked})}
                        disabled={!editingConfig}
                        className="w-4 h-4"
                      />
                      <span className="font-medium">Ticker Enabled</span>
                    </label>
                  </div>

                  {/* Copy Blocks */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Headline
                    </label>
                    <Input
                      value={editingConfig ? configUpdates.headline : config.headline}
                      onChange={(e) => setConfigUpdates({...configUpdates, headline: e.target.value})}
                      disabled={!editingConfig}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subheadline
                    </label>
                    <Input
                      value={editingConfig ? configUpdates.subheadline : config.subheadline}
                      onChange={(e) => setConfigUpdates({...configUpdates, subheadline: e.target.value})}
                      disabled={!editingConfig}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trust Strip
                    </label>
                    <Input
                      value={editingConfig ? configUpdates.trust_strip : config.trust_strip}
                      onChange={(e) => setConfigUpdates({...configUpdates, trust_strip: e.target.value})}
                      disabled={!editingConfig}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminFoundersPanel;
